import { useCallback, useEffect, useRef, useState } from "react";

// Device position for "сколько до водителя".
// The browser geolocation API needs HTTPS (or localhost); a denial, an old
// phone or a missing permission all end with `status: "error"` and the UI
// simply hides distances instead of blocking the screen.

// Android 12+ can grant only approximate location: the fix then carries an
// accuracy of about 2 km. Such a point is a network guess, so a "330 m to the
// driver" line built from it would be fiction. Distances need a precise fix;
// the UI asks the person to allow precise location in the phone settings.
export const PRECISE_ACCURACY_M = 150;
export const isPreciseFix = (point) =>
  Boolean(point) &&
  Number.isFinite(point.accuracy) &&
  point.accuracy <= PRECISE_ACCURACY_M;
export const useGeolocation = ({ enabled = true, watch = false } = {}) => {
  const supported =
    typeof navigator !== "undefined" && Boolean(navigator.geolocation);
  const [position, setPosition] = useState(null);
  const [status, setStatus] = useState(supported ? "idle" : "unsupported");
  const [error, setError] = useState(null);
  // The last fix is kept in a ref too: callers hand it to a socket handler
  // that may be registered before the next state render
  const lastRef = useRef(null);

  const accept = useCallback((coords) => {
    const next = {
      lat: coords.latitude,
      lng: coords.longitude,
      ...(Number.isFinite(coords.accuracy) && { accuracy: Math.round(coords.accuracy) }),
    };
    lastRef.current = next;
    setPosition(next);
    setStatus("ready");
    return next;
  }, []);

  const request = useCallback(
    () =>
      new Promise((resolve) => {
        if (!supported) {
          setStatus("unsupported");
          resolve(null);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (result) => resolve(accept(result.coords)),
          (failure) => {
            setError(
              failure.code === 1
                ? "Разрешите доступ к геопозиции, чтобы видеть, сколько ехать водителю"
                : "Не удалось определить геопозицию"
            );
            setStatus("error");
            resolve(null);
          },
          // Ask for the best fix the device can give; Android may still answer
          // with an approximate one, and the accuracy check catches that
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 30 * 1000 }
        );
      }),
    [accept, supported]
  );

  useEffect(() => {
    if (!enabled || !watch || !supported) return undefined;
    const id = navigator.geolocation.watchPosition(
      (result) => accept(result.coords),
      () => undefined,
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 15 * 1000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [accept, enabled, supported, watch]);

  return {
    supported,
    position,
    status,
    error,
    request,
    // Latest fix without waiting for a render (socket handlers read this)
    current: () => lastRef.current ?? position,
  };
};

export const useDriverHeartbeat = ({ socket, onLine, driverGeo }) => {
  const sentRef = useRef(null);
  useEffect(() => {
    if (!onLine) {
      sentRef.current = null;
      return;
    }
    driverGeo.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLine]);

  useEffect(() => {
    if (!onLine) return undefined;
    const send = () => {
      const point = driverGeo.current();
      if (point) sentRef.current = point;
      socket.emit("driver:heartbeat", point ?? {});
    };
    const sendIfMoved = () => {
      const point = driverGeo.current();
      if (!point) return;
      const previous = sentRef.current;
      const moved =
        !previous ||
        Math.abs(previous.lat - point.lat) > 0.00045 ||
        Math.abs(previous.lng - point.lng) > 0.0007;
      if (moved) send();
    };
    send();
    const timer = setInterval(send, 30 * 1000);
    const watcher = setInterval(sendIfMoved, 5 * 1000);
    return () => {
      clearInterval(timer);
      clearInterval(watcher);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLine, socket]);
};

export default useGeolocation;
